import React, { useState, useRef, useEffect, useLayoutEffect, Component, ReactNode, ErrorInfo } from 'react';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Check, CheckCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { useGlobalState } from '../../GlobalStateProvider';
import { messagesStore } from '@/data/storage/messages/adapter';
import type { MessageThread, ChatMessage as StoredMsg } from '@/types/messages';
import { getSupabase } from '@/lib/supabase';
import { reportSwallowed } from '@/lib/sentry';
import { toast } from 'sonner';
import { rollbackOptimisticSend } from './rollbackOptimisticSend';

const NEAR_BOTTOM_PX = 8; // tighter threshold for client chat to avoid unwanted snapping
const MAX_MESSAGE_CHARS = 10000; // Character limit (not bytes)

// =============================================================================
// Error Boundary for Client Chat
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorId: string | null;
}

class ClientChatErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return {
      hasError: true,
      errorId: `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
    };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // Log without exposing PII
    console.error(
      JSON.stringify({
        ts: new Date().toISOString(),
        level: 'error',
        event: 'client_chat.error_boundary',
        errorId: this.state.errorId,
      }),
    );
  }

  handleReset = (): void => {
    this.setState({ hasError: false, errorId: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Card className="h-full flex items-center justify-center">
          <CardContent className="text-center p-8 max-w-md">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <h3 className="font-semibold text-lg mb-2">Something went wrong</h3>
            <p className="text-muted-foreground text-sm mb-4">
              We encountered an error loading the chat. Please try again.
            </p>
            <p className="text-xs text-muted-foreground mb-6 font-mono">Reference: {this.state.errorId}</p>
            <Button onClick={this.handleReset} variant="outline" className="gap-2" aria-label="Try loading chat again">
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: Date;
  isRead: boolean;
  type: 'text' | 'template';
}

interface ClientChatProps {
  coachName?: string;
  coachInitials?: string;
  messages?: Message[];
  onSendMessage?: (content: string, type?: string) => void;
}

function ClientChatInner({
  coachName: propCoachName,
  coachInitials: propCoachInitials,
  messages = [],
  onSendMessage: _onSendMessage,
}: ClientChatProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gs = useGlobalState() as any;
  // Ensure client mode when viewing client chat
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (gs?.userType !== 'client') gs?.setUserType?.('client');
    } catch {
      // OBS-11 a-ignore: defensive guard around local UI state updates; nothing here fails in a way worth reporting.
    }
  }, [gs?.userType]);

  // Get current client from authenticated user
  const clientId: string = gs?.authUser?.id || '';

  // Resolve coach/thread with race-safety
  const [coachId, setCoachId] = useState<string | null>(null);
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [loadingThread, setLoadingThread] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  // Message cache (threadId -> mapped messages)
  const messageCacheRef = useRef<Map<string, Message[]>>(new Map());
  // Identity lock (prevents drift during async ops)
  const activeParticipantsRef = useRef<{ threadId?: string; clientId?: string; coachId?: string }>({});
  // Run token to ignore stale async operations
  const openThreadRunTokenRef = useRef<number>(0);
  // (poll merge uses stableMessageKeyRef below for stable React keys)

  const coachName = propCoachName || gs?.currentCoach?.name || 'Coach';
  const coachInitials =
    propCoachInitials ||
    (gs?.currentCoach?.name
      ? gs.currentCoach.name
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
      : 'AC');
  const [message, setMessage] = useState('');
  const sendingRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [nearBottom, setNearBottom] = useState(true);
  const [newSinceSeen, setNewSinceSeen] = useState(0);
  const nearBottomRef = useRef<boolean>(true);
  const lastSeenAtRef = useRef<number>(0);

  // Aria-live announcements
  const [announcement, setAnnouncement] = useState('');

  const isNearBottom = (el: HTMLElement, threshold = NEAR_BOTTOM_PX) =>
    el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

  const handleScroll = () => {
    const el = listRef.current as HTMLElement | null;
    if (!el) return;
    const nb = isNearBottom(el);
    nearBottomRef.current = nb;
    setNearBottom(nb);
    if (nb) setNewSinceSeen(0);
  };

  const currentUserId = clientId;
  const [chatMessages, setChatMessages] = useState<Message[]>(messages);
  // Track optimistic sends so we can reconcile temp ids once the server echoes the message back
  const pendingSendsRef = useRef<Array<{ tempId: string; body: string; atMs: number }>>([]);
  // Keep a stable render key across "temp -> server" swaps to prevent flicker with AnimatePresence
  const stableMessageKeyRef = useRef<Map<string, string>>(new Map());

  // Resolve coachId once (prefer existing threads/program assignment; DO NOT use gs.currentCoach.id in client mode)
  useEffect(() => {
    let alive = true;
    async function resolveCoach() {
      if (!clientId) return;
      setLoadingThread(true);
      try {
        const threads = await messagesStore.getThreadsByClient(String(clientId));
        const firstCoachId = (threads || []).find(
          (t: any) => t?.coachId && String(t.coachId) !== String(clientId),
        )?.coachId;
        if (firstCoachId && String(firstCoachId) !== String(clientId)) {
          if (!alive) return;
          setCoachId(String(firstCoachId));
          return;
        }
        const response = await fetch('/api/clients/programs', { credentials: 'include' });
        if (response.ok) {
          const { ownerCoachId } = await response.json();
          if (ownerCoachId && String(ownerCoachId) !== String(clientId)) {
            if (!alive) return;
            setCoachId(String(ownerCoachId));
            return;
          }
        }
        if (!alive) return;
        setCoachId(null);
        setLoadingThread(false); // Only set false when coach resolution completes with no coach
      } catch (e) {
        if (!alive) return;
        console.error('[client-chat] Failed to resolve coach:', e);
        setCoachId(null);
      }
    }
    resolveCoach();
    return () => {
      alive = false;
    };
  }, [clientId]);

  async function openThreadForClient() {
    if (!clientId || !coachId) return;
    const runToken = ++openThreadRunTokenRef.current;
    try {
      setLoadingThread(true);
      setChatMessages([]);
      setShouldScrollToBottom(false);

      const existing = await messagesStore.getThreadByPair(String(clientId), String(coachId));
      const t = existing || (await messagesStore.createThread(String(clientId), String(coachId)));
      if (!t?.id) {
        if (openThreadRunTokenRef.current === runToken) setLoadingThread(false);
        return;
      }

      const threadId = String(t.id);
      setThread(t);
      activeParticipantsRef.current = { threadId, clientId: String(clientId), coachId: String(coachId) };

      const cached = messageCacheRef.current.get(threadId);
      if (cached && cached.length) {
        setChatMessages(cached);
        setShouldScrollToBottom(true);
        setLoadingThread(false);
      }

      const rows: StoredMsg[] = await messagesStore.getMessages(threadId);
      const mapped: Message[] = rows.map((m) => ({
        id: m.id,
        senderId: m.authorType === 'coach' ? 'coach' : String(clientId),
        senderName: m.authorType === 'coach' ? coachName : 'You',
        content: m.body,
        timestamp: new Date(m.createdAt),
        isRead: !!(m.readBy || []).includes(String(clientId)),
        type: 'text',
      }));

      // Always cache (even if stale)
      messageCacheRef.current.set(threadId, mapped);

      if (openThreadRunTokenRef.current !== runToken) {
        // Stale run; keep cache but don't touch UI
        return;
      }

      setChatMessages(mapped);
      setShouldScrollToBottom(true);
      setLoadingThread(false);

      const lastRow = rows[rows.length - 1];
      lastSeenAtRef.current = lastRow ? new Date(lastRow.createdAt).getTime() : 0;
    } catch (e) {
      console.error('[client-chat] Failed to open thread:', e);
      if (openThreadRunTokenRef.current === runToken) setLoadingThread(false);
    }
  }

  // Poll for new messages when thread is active
  useEffect(() => {
    const threadId = thread?.id ? String(thread.id) : '';
    if (!threadId) return;

    let alive = true;

    const pollMessages = async () => {
      try {
        const rows: StoredMsg[] = await messagesStore.getMessages(threadId);
        if (!alive) return;

        // Guard against drift
        if (activeParticipantsRef.current.threadId !== threadId) return;

        const mapped: Message[] = rows.map((m) => ({
          id: m.id,
          senderId: m.authorType === 'coach' ? 'coach' : String(clientId),
          senderName: m.authorType === 'coach' ? coachName : 'You',
          content: m.body,
          timestamp: new Date(m.createdAt),
          isRead: !!(m.readBy || []).includes(String(clientId)),
          type: 'text',
        }));

        setChatMessages((prev) => {
          if (activeParticipantsRef.current.threadId !== threadId) return prev;

          // Match pending temps to server messages by content + timestamp
          const pending = pendingSendsRef.current;
          const confirmedTempIds = new Set<string>();
          for (const p of pending) {
            const hit = mapped.find(
              (m) =>
                m.senderId === String(clientId) &&
                m.content === p.body &&
                Math.abs(m.timestamp.getTime() - p.atMs) <= 15000,
            );
            if (hit) {
              confirmedTempIds.add(p.tempId);
              stableMessageKeyRef.current.set(hit.id, p.tempId);
            }
          }
          if (confirmedTempIds.size > 0) {
            pendingSendsRef.current = pending.filter((pp) => !confirmedTempIds.has(pp.tempId));
          }

          const serverIds = new Set(mapped.map((m) => m.id));
          const survivingTemps = prev.filter(
            (m) => m.id.startsWith('temp_') && !serverIds.has(m.id) && !confirmedTempIds.has(m.id),
          );

          const merged = survivingTemps.length > 0 ? [...mapped, ...survivingTemps] : mapped;

          if (prev.length === merged.length && prev.every((m, i) => m.id === merged[i]?.id)) {
            return prev;
          }
          return merged;
        });
      } catch (e) {
        if (alive) console.error('[client-chat] Polling failed:', e);
      }
    };

    const pollInterval = setInterval(pollMessages, 1500);
    pollMessages();

    const onFocus = () => {
      if (alive) pollMessages();
    };
    window.addEventListener('focus', onFocus);

    let realtimeChannel: ReturnType<NonNullable<ReturnType<typeof getSupabase>>['channel']> | null = null;
    try {
      const sb = getSupabase();
      if (sb) {
        realtimeChannel = sb
          .channel(`client-chat-${threadId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'messages',
              filter: `thread_id=eq.${threadId}`,
            },
            () => {
              if (alive) pollMessages();
            },
          )
          .subscribe();
      }
    } catch {
      /* Realtime not available — polling is the fallback */
    }

    return () => {
      alive = false;
      clearInterval(pollInterval);
      window.removeEventListener('focus', onFocus);
      if (realtimeChannel) {
        try {
          getSupabase()?.removeChannel(realtimeChannel);
        } catch {
          // OBS-11 a-ignore: teardown; the channel/listener may already be closed.
        }
      }
    };
  }, [thread?.id, clientId, coachName]);

  // Open thread once coachId is known
  useEffect(() => {
    if (!coachId) {
      // Don't set loading false here - let resolveCoach manage it
      return;
    }
    openThreadForClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  // Cross-tab sync (thread-aware, no stale closures)
  useEffect(() => {
    const threadId = thread?.id ? String(thread.id) : '';
    if (!threadId) return;
    const onFocus = () => {
      try {
        gs.markThreadRead?.({ threadId, userType: 'client', userId: clientId });
      } catch {
        // OBS-11 a-ignore: only guards a sync throw; the call is async and not awaited, so a rejection surfaces as an unhandled rejection, which Sentry captures.
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (!e) return;
      if (e.key === 'coachd-messages') {
        messagesStore.getMessages(threadId).then((rows) => {
          // Guard against drift
          if (activeParticipantsRef.current.threadId !== threadId) return;
          const mapped: Message[] = (rows || []).map((m: any) => ({
            id: m.id,
            senderId: m.authorType === 'coach' ? 'coach' : String(clientId),
            senderName: m.authorType === 'coach' ? coachName : 'You',
            content: m.body,
            timestamp: new Date(m.createdAt),
            isRead: !!(m.readBy || []).includes(String(clientId)),
            type: 'text',
          }));
          setChatMessages((prev) => {
            const hasTemp = prev.some((m) => m.id.startsWith('temp_'));
            if (hasTemp) return prev;
            if (activeParticipantsRef.current.threadId !== threadId) return prev;
            return mapped;
          });
        });
      }
    };
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('coachd-messages');
      bc.onmessage = (ev: any) => {
        const { key, payload } = ev.data || {};
        if (key === 'coachd-messages' && payload?.threadId && String(payload.threadId) === threadId) {
          messagesStore.getMessages(threadId).then((rows) => {
            if (activeParticipantsRef.current.threadId !== threadId) return;
            const mapped: Message[] = (rows || []).map((m: any) => ({
              id: m.id,
              senderId: m.authorType === 'coach' ? 'coach' : String(clientId),
              senderName: m.authorType === 'coach' ? coachName : 'You',
              content: m.body,
              timestamp: new Date(m.createdAt),
              isRead: !!(m.readBy || []).includes(String(clientId)),
              type: 'text',
            }));
            setChatMessages((prev) => {
              const hasTemp = prev.some((m) => m.id.startsWith('temp_'));
              if (hasTemp) return prev;
              if (activeParticipantsRef.current.threadId !== threadId) return prev;
              return mapped;
            });
          });
        }
      };
    } catch {
      // OBS-11 a-ignore: BroadcastChannel is missing in some browsers and throws once closed; cross-tab sync is an enhancement, polling still refreshes.
    }

    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      try {
        if (bc) bc.onmessage = null as any;
      } catch {
        // OBS-11 a-ignore: teardown; the channel/listener may already be closed.
      }
    };
  }, [thread?.id, clientId, coachName, gs]);

  // Mark as read when user is at/near bottom for the active thread
  useEffect(() => {
    const threadId = thread?.id ? String(thread.id) : '';
    if (!threadId) return;
    if (!nearBottom) return;
    try {
      gs.markThreadRead?.({ threadId, userType: 'client', userId: clientId });
    } catch {
      // OBS-11 a-ignore: only guards a sync throw; the call is async and not awaited, so a rejection surfaces as an unhandled rejection, which Sentry captures.
    }
  }, [nearBottom, thread?.id, clientId, gs]);

  // Track near bottom and show floater (also attach handler via onScroll)
  useEffect(() => {
    const el = listRef.current as HTMLElement | null;
    if (!el) return;
    let raf = 0 as any;
    const onScrollEvt = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        handleScroll();
      });
    };
    el.addEventListener('scroll', onScrollEvt, { passive: true } as any);
    return () => {
      el.removeEventListener('scroll', onScrollEvt as any);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = listRef.current as HTMLElement | null;
    if (!el) return;
    const last = chatMessages[chatMessages.length - 1];
    const lastTs = last ? last.timestamp.getTime() : 0;
    const iSentLast = last?.senderId === currentUserId;
    // Only auto-scroll on new messages if the user was already near bottom or we sent the last message
    if (nearBottomRef.current || iSentLast) {
      lastSeenAtRef.current = lastTs;
      el.scrollTop = el.scrollHeight;
      setNewSinceSeen(0);
    } else {
      const unseen = chatMessages.filter(
        (m) => m.senderId === 'coach' && m.timestamp.getTime() > lastSeenAtRef.current,
      ).length;
      setNewSinceSeen(unseen);
    }
  }, [chatMessages, currentUserId]);

  useLayoutEffect(() => {
    if (!shouldScrollToBottom) return;
    const el = listRef.current as HTMLElement | null;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    setShouldScrollToBottom(false);
  }, [shouldScrollToBottom, chatMessages.length]);

  const handleSendMessage = async () => {
    const body = message.trim();
    if (!body) return;
    if (sendingRef.current) return;

    // Character limit validation (not bytes - supports emoji properly)
    const charCount = [...body].length;
    if (charCount > MAX_MESSAGE_CHARS) {
      // Simple toast for client chat (show character count)
      setAnnouncement(`Message too long: ${charCount} of ${MAX_MESSAGE_CHARS} characters`);
      return;
    }

    // Declared outside the try so the catch can roll the optimistic bubble back.
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      sendingRef.current = true;
      // Optimistic local append
      pendingSendsRef.current.push({ tempId, body, atMs: Date.now() });
      stableMessageKeyRef.current.set(tempId, tempId);
      setChatMessages((prev) => {
        const next: Message[] = [
          ...prev,
          {
            id: tempId,
            senderId: currentUserId,
            senderName: 'You',
            content: body,
            timestamp: new Date(),
            isRead: true,
            type: 'text' as const,
          },
        ];
        return next;
      });
      setShouldScrollToBottom(true);

      // Clear the input immediately (before any awaits) so quick follow-up typing doesn't append to prior text.
      setMessage('');

      if (thread?.id) {
        await gs?.sendMessage?.({
          threadId: String(thread.id),
          clientId: String(clientId),
          coachId: String(coachId),
          body,
        });
      } else if (coachId) {
        await gs?.sendMessage?.({ clientId: String(clientId), coachId: String(coachId), body });
      }

      // Announce success for screen readers
      setAnnouncement('Message sent');
      setTimeout(() => setAnnouncement(''), 1000);

      // Return focus to textarea
      textareaRef.current?.focus();
      // Polling will reconcile; do a one-off refresh if we're on a thread
      if (thread?.id) {
        const tid = String(thread.id);
        const rows: StoredMsg[] = await messagesStore.getMessages(tid);
        if (activeParticipantsRef.current.threadId === tid) {
          const mapped: Message[] = rows.map((m) => ({
            id: m.id,
            senderId: m.authorType === 'coach' ? 'coach' : String(clientId),
            senderName: m.authorType === 'coach' ? coachName : 'You',
            content: m.body,
            timestamp: new Date(m.createdAt),
            isRead: !!(m.readBy || []).includes(String(clientId)),
            type: 'text',
          }));
          messageCacheRef.current.set(tid, mapped);
          setChatMessages((prev) => {
            const prevTemps = prev.filter((m) => String(m.id || '').startsWith('temp_'));
            const mappedById = new Set(mapped.map((m) => String(m.id)));
            const _nowMs = Date.now();
            const pending = pendingSendsRef.current || [];

            // Consider a temp "confirmed" if the server has a client message with same body within ~10s
            const confirmedTempIds = new Set<string>();
            const confirmations: Array<{ tempId: string; serverId: string }> = [];
            for (const p of pending) {
              const hit = mapped.find(
                (mm) =>
                  mm.senderId === String(clientId) &&
                  mm.content === p.body &&
                  Math.abs(mm.timestamp.getTime() - p.atMs) <= 10000,
              );
              if (hit) {
                confirmedTempIds.add(p.tempId);
                confirmations.push({ tempId: p.tempId, serverId: String(hit.id) });
                // Use the temp id as the stable render key for the server id to avoid unmount/remount flicker
                stableMessageKeyRef.current.set(String(hit.id), p.tempId);
              }
            }
            if (confirmedTempIds.size > 0) {
              pendingSendsRef.current = pending.filter((p) => !confirmedTempIds.has(p.tempId));
            }

            const keepTemps = prevTemps.filter(
              (t) => !confirmedTempIds.has(String(t.id)) && !mappedById.has(String(t.id)),
            );
            const merged = [...mapped, ...keepTemps].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

            return merged;
          });
          setShouldScrollToBottom(true);
        }
      }
    } catch (err) {
      // OBS-11: this used to be empty, so a failed send left the bubble on screen looking
      // sent while the message never reached the coach. Same handling as coach Chat.tsx.
      // TODO(component-tests): send fails → bubble removed, text restored, toast shown, reported (OBS-11 bug). Needs React Testing Library — see the component-testing ticket.
      reportSwallowed('ClientChat.handleSendMessage', err);
      setChatMessages((prev) => rollbackOptimisticSend(prev, [], tempId).messages);
      pendingSendsRef.current = rollbackOptimisticSend([], pendingSendsRef.current, tempId).pendingSends;
      setMessage((current) => current || body); // give the text back unless they've started typing again
      toast.error('Failed to send — please try again.');
    } finally {
      sendingRef.current = false;
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="h-[calc(100vh-8rem)] min-h-0 flex flex-col"
    >
      <Card className="h-full min-h-0 flex flex-col overflow-hidden">
        <CardHeader className="pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="w-10 h-10">
                <AvatarFallback>{coachInitials}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
            </div>
            <div>
              <h3 className="font-semibold">{coachName}</h3>
              <p className="text-sm text-muted-foreground">Your Coach</p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 min-h-0 flex flex-col p-0 overflow-hidden">
          <div
            ref={(el) => {
              (listRef as any).current = el as HTMLDivElement;
            }}
            onScroll={handleScroll}
            className="flex-1 h-full overflow-y-auto overscroll-contain relative"
          >
            <div className="px-4 pt-6 pb-4">
              <div className="space-y-4">
                {loadingThread && chatMessages.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    {/* Skeleton loader for messages */}
                    <div className="space-y-4">
                      <div className="flex justify-start">
                        <div className="h-16 w-64 bg-muted/50 rounded-lg animate-pulse"></div>
                      </div>
                      <div className="flex justify-end">
                        <div className="h-16 w-48 bg-muted/50 rounded-lg animate-pulse"></div>
                      </div>
                      <div className="flex justify-start">
                        <div className="h-20 w-72 bg-muted/50 rounded-lg animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                )}
                {!loadingThread && chatMessages.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">No messages yet.</div>
                )}
                <AnimatePresence>
                  {chatMessages.map((msg) => (
                    <motion.div
                      key={stableMessageKeyRef.current.get(String(msg.id)) ?? String(msg.id)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className={`flex ${msg.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md ${msg.senderId === currentUserId ? 'order-2' : 'order-1'}`}>
                        <div
                          className={`rounded-lg p-3 ${
                            msg.senderId === currentUserId ? 'bg-primary text-primary-foreground ml-auto' : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm break-words">{msg.content}</p>
                          <div
                            className={`flex items-center gap-1 mt-2 ${
                              msg.senderId === currentUserId ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <span
                              className={`text-xs ${
                                msg.senderId === currentUserId ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}
                            >
                              {formatTime(msg.timestamp)}
                            </span>
                            {msg.senderId === currentUserId && (
                              <div className="text-primary-foreground/70">
                                {msg.isRead ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>
            </div>
            {newSinceSeen > 0 && !nearBottom && (
              <button
                aria-live="polite"
                className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-full border border-[#232329] bg-[#151518] px-3 py-1 text-sm text-[#EDEDEF] shadow"
                onClick={() => {
                  const el = listRef.current as HTMLElement | null;
                  if (el) el.scrollTop = el.scrollHeight;
                  setNewSinceSeen(0);
                }}
              >
                {newSinceSeen} new message{newSinceSeen > 1 ? 's' : ''} — Jump to latest
              </button>
            )}
          </div>

          <div className="border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMessage(v);
                  }}
                  placeholder="Type your message..."
                  className="min-h-10 max-h-24 pr-12 resize-none"
                  aria-label="Message input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="absolute right-2 bottom-2"
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Aria-live announcements for screen readers */}
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * Client Chat component with error boundary
 */
export function ClientChat(props: ClientChatProps) {
  return (
    <ClientChatErrorBoundary>
      <ClientChatInner {...props} />
    </ClientChatErrorBoundary>
  );
}
